import React, { useEffect, useState } from 'react'
import ADD from './assets/add.svg'
import TaskStar from './assets/taskStart.svg'
import { Trash2Icon, Loader2Icon } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import TaskModal from './components/TaskModal'
import DeleteModal from '../../components/atomic/DeleteModal'
import { createTaskAsync, fetchTasksAsync, deleteTaskAsync, selectTask } from '../../slice/taskSlice'
import { fetchAllOrganizations } from '../../slice/organizationsSlice'
import { useToast } from '../../components/Toast'



const ManageTasks = ({ setShowAddTask }) => {
  const dispatch = useDispatch();
  const { addToast, ToastContainer } = useToast();
  const tasks = useSelector(state => state.tasks.tasks);
  const status = useSelector(state => state.tasks.status);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const { data, loading, error } = useSelector((state) => state.admin);
  const { organizations } = useSelector((state) => state.organizations);

  const isSuperAdmin = data?.user_role === "SUPER_ADMIN";
  const isAdmin = data?.user_role === "ADMIN";
  const userOrganizationId = data?.organization_id;

  // Filter tasks based on user role
  const filteredTasks = tasks.filter(task => {
    // Always exclude task with ID 40
    if (task.id === 40) {
      return false;
    }
    
    if (isSuperAdmin) {
      // Super admin can see all tasks from any organization
      return true;
    } else if (isAdmin) {
      // Admin can only see tasks from their organization (not global tasks)
      return task.organization_id === userOrganizationId;
    }
    // Regular users shouldn't see this page, but just in case
    return false;
  });

  useEffect(() => {
    // Fetch tasks and organizations when component mounts
    const userEmail = localStorage.getItem('email');
    
    // For super admin, fetch all tasks without user email filter
    // For admin, fetch tasks filtered by their organization
    if (isSuperAdmin) {
      dispatch(fetchTasksAsync()); // No user email = all tasks
    } else {
      dispatch(fetchTasksAsync(userEmail)); // With user email = org-specific + global
    }
    
    dispatch(fetchAllOrganizations());
  }, [dispatch, isSuperAdmin]);

  const handleAddTask = async (taskName, organizationId) => {
    if (!taskName.trim()) {
      addToast('Task name cannot be empty', 'error');
      return;
    }

    try {
      const userEmail = localStorage.getItem('email');
      const taskData = { 
        name: taskName,
        organization_id: organizationId
      };
      
      await dispatch(createTaskAsync(taskData)).unwrap();
      addToast('Aufgabe erfolgreich erstellt', 'success');
      setNewTaskName('');
      setIsModalOpen(false);
      // Refresh the task list
      dispatch(fetchTasksAsync(userEmail));
    } catch (err) {
      addToast(err.message || 'Failed to create task', 'error');
    }
  };

  const handleDelete = async (taskId) => {
    setTaskToDelete(taskId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteTaskAsync(taskToDelete)).unwrap();
      addToast('Aufgabe erfolgreich gelöscht', 'success');
    } catch (err) {
      addToast(err.message || 'Failed to delete task', 'error');
    }
    setIsDeleteModalOpen(false);
    setTaskToDelete(null);
  };

  const handleTaskSelect = (task) => {
    // Dispatch action to select task in Redux store
    dispatch(selectTask(task));
    // Use setShowAddTask to switch to AddTasks
    setShowAddTask(true);
  };

  return (
    <div className='mb-16 pt-5 mt-8 mx-auto px-4 sm:px-6 rounded lg:px-8'>
      <ToastContainer />
      <div className='pl-32 pr-40'>
        <div className='flex justify-between items-center mb-8'>
          <h1 className='text-2xl font-semibold mb-6'>Aufgaben verwalten</h1>
          {isSuperAdmin && (
            <button
              onClick={() => setIsModalOpen(true)}
              className='flex items-center font-bold rounded-lg px-4 py-2 bg-custom-yellow hover:bg-yellow-500 transition-colors shadow-sm'
            >
              <img src={ADD} alt="add" />
              <p className='ml-2 text-white'>Aufgabe hinzufügen</p>
            </button>
          )}
        </div>
        <div className="overflow-y-auto max-h-[60vh] no-scrollbar">
          <ul className="flex flex-col gap-2">
            {filteredTasks.map((task) => (
              <li
                key={task.id}
                onClick={() => handleTaskSelect(task)}
                className="px-4 py-3 flex justify-between items-center cursor-pointer bg-custom-white_light rounded-lg hover:bg-gray-50"
              >
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <img src={TaskStar} alt="" /> 
                  <div className="flex flex-col">
                    <span>{task.name}</span>
                    <span className="text-xs text-gray-500">
                      {task.organization_id ? 
                        organizations.find(org => org.organization_id === task.organization_id)?.organization_name || 'Organisation' 
                        : 'Global'}
                    </span>
                  </div>
                </span>
                {deletingTaskId === task.id ? (
                  <Loader2Icon className='animate-spin' height={20} width={20} color='gray' />
                ) : (
                  isSuperAdmin && (
                    <Trash2Icon
                      className='cursor-pointer'
                      height={20}
                      width={20}
                      color='gray'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(task.id);
                      }}
                    />
                  )
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <TaskModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onConfirm={(taskName, organizationId) => {
          handleAddTask(taskName, organizationId);
        }}
        title="Aufgabe hinzufügen"
        description="Geben Sie einen Namen ein und wählen Sie eine Organisation (optional)."
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setTaskToDelete(null);
        }}
        onDelete={confirmDelete}
        title="Aufgabe löschen"
        message="Sind Sie sicher, dass Sie diese Aufgabe löschen möchten?"
      />
    </div>
  )
}

export default ManageTasks